import * as v from "valibot";

const invalid = "بيانات غير صحيحة";
const invalidConnection = "بيانات اتصال غير صحيحة";
export const IdSchema = v.pipe(
  v.string(invalid),
  v.minLength(1, invalid),
  v.maxLength(80, invalid),
);
export const AvailabilitySchema = v.picklist(
  ["online", "busy", "offline"],
  "حالة غير صحيحة",
);
const LastActiveSchema = v.nullable(
  v.pipe(v.number(), v.integer(), v.minValue(0)),
);
export const CallMediaSchema = v.picklist(
  ["audio", "video"],
  "نوع المكالمة غير صحيح",
);
export const CallStatusSchema = v.picklist([
  "ringing",
  "active",
  "ended",
  "missed",
  "declined",
  "cancelled",
  "disconnected",
]);
// DiceBear "adventurer" presets generated into src/assets/avatars by
// scripts/generate-avatars.mjs; the ID names the file.
export const avatarGenders = ["male", "female"] as const;
const avatarNumbers = [
  ...["01", "02", "03", "04", "05", "06", "07", "08"],
  ...["09", "10", "11", "12", "13", "14", "15"],
] as const;
export const avatars = avatarGenders.flatMap((gender) =>
  avatarNumbers.map((number) => `${gender}-${number}` as const),
);
export const AvatarSchema = v.picklist(avatars, "صورة غير صحيحة");
// Twelve Crockford base32 characters in groups of four, e.g. 7hq2-xk9m-pd4w.
const linkCode = (message: string) =>
  v.pipe(
    v.string(message),
    v.regex(/^[0-9a-hjkmnp-tv-z]{4}(?:-[0-9a-hjkmnp-tv-z]{4}){2}$/, message),
  );
export const CallLinkIdSchema = linkCode("رابط المكالمة غير صحيح");
export const ProfileCodeSchema = linkCode("رابط الحساب غير صحيح");
export const GroupLinkIdSchema = linkCode("رابط المكالمة الجماعية غير صحيح");
export const ChatMessageSchema = v.object({
  id: IdSchema,
  sender: IdSchema,
  recipient: IdSchema,
  body: v.pipe(v.string(), v.minLength(1), v.maxLength(2000)),
  created_at: v.number(),
});
export const PersonSchema = v.object({
  id: IdSchema,
  name: v.string(),
  username: v.string(),
  // Only the account's own snapshot carries this; it times the username cooldown.
  username_changed_at: v.optional(v.nullable(v.number())),
  // The account's own snapshot too: when it joined, shown on its account page.
  created_at: v.optional(v.number()),
  color: v.string(),
  avatar: v.optional(v.nullable(AvatarSchema)),
  status: AvailabilitySchema,
  last_active_at: v.optional(LastActiveSchema),
  show_last_active: v.optional(v.picklist([0, 1])),
  // Also the account's own snapshot only: whether username search finds it,
  // and the code in its profile link once that has been shared.
  discoverable: v.optional(v.picklist([0, 1])),
  link_code: v.optional(v.nullable(ProfileCodeSchema)),
  bio: v.optional(v.string()),
  relation: v.optional(v.nullable(v.picklist(["pending", "accepted"]))),
  requester: v.optional(v.nullable(IdSchema)),
  requested_by: v.optional(IdSchema),
  has_recovery: v.optional(v.picklist([0, 1])),
  // Friends only: the latest message either way and messages unread by the viewer.
  last_message: v.optional(v.nullable(ChatMessageSchema)),
  unread: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0))),
  // Friends only: the viewer's messages up to this time are read by this person.
  // A message's own time rather than when they read it, which could reveal
  // activity from while they were hidden.
  read_through: v.optional(v.nullable(v.number())),
  // Friends and call-link hosts only: calls ring this person through a
  // notification when no Ranna tab is open.
  call_notifications: v.optional(v.boolean()),
  // Someone who joined a call link without an account: a name and nothing else.
  guest: v.optional(v.literal(true)),
});
export const LocaleSchema = v.picklist(["ar", "en"], invalid);
// PushSubscription.toJSON() from the browser. Only the browsers' own push
// services are accepted, so the Worker never posts to arbitrary hosts.
export const PushSubscriptionSchema = v.object({
  endpoint: v.pipe(
    v.string(invalid),
    v.maxLength(1000, invalid),
    v.regex(
      /^https:\/\/(?:fcm\.googleapis\.com|updates\.push\.services\.mozilla\.com|web\.push\.apple\.com|[a-z0-9-]+\.notify\.windows\.com)\//,
      "هذا المتصفح لا يدعم الإشعارات",
    ),
  ),
  keys: v.object({
    // An uncompressed P-256 public key and a 16-byte secret, base64url.
    p256dh: v.pipe(v.string(invalid), v.regex(/^[\w-]{87}=?$/, invalid)),
    auth: v.pipe(v.string(invalid), v.regex(/^[\w-]{22}(?:==)?$/, invalid)),
  }),
});

// Keep the wire format as JSON text; these parsers also validate its contents.
function jsonSchema<T extends v.GenericSchema>(
  schema: T,
  max: number,
  message = invalidConnection,
) {
  return v.pipe(
    v.string(message),
    v.maxLength(max, message),
    v.parseJson(undefined, message),
    schema,
  );
}
const OfferSchema = v.object({
  type: v.literal("offer"),
  sdp: v.string(),
});
const AnswerSchema = v.object({
  type: v.literal("answer"),
  sdp: v.string(),
});
export const OfferJsonSchema = jsonSchema(OfferSchema, 30000);
export const AnswerJsonSchema = jsonSchema(AnswerSchema, 30000);
export const RevisionSchema = v.pipe(v.number(), v.integer(), v.minValue(0));
export const OfferTextSchema = v.pipe(
  v.string(invalidConnection),
  v.check((text) => v.is(OfferJsonSchema, text), invalidConnection),
);
export const AnswerTextSchema = v.pipe(
  v.string(invalidConnection),
  v.check((text) => v.is(AnswerJsonSchema, text), invalidConnection),
);
const CallRecoverySchema = v.object({
  revision: RevisionSchema,
  startedAt: v.number(),
  offer: v.nullable(OfferTextSchema),
  answer: v.nullable(AnswerTextSchema),
});
export const IceCandidateSchema = v.object(
  {
    candidate: v.string(invalidConnection),
    sdpMid: v.optional(v.nullable(v.string(invalidConnection))),
    sdpMLineIndex: v.optional(
      v.nullable(
        v.pipe(
          v.number(invalidConnection),
          v.integer(invalidConnection),
          v.minValue(0, invalidConnection),
          v.maxValue(65535, invalidConnection),
        ),
      ),
    ),
    usernameFragment: v.optional(v.nullable(v.string(invalidConnection))),
  },
  invalidConnection,
);
export const IceCandidateTextSchema = v.pipe(
  v.string(),
  v.maxLength(3000, "بيانات الاتصال أكبر من المسموح"),
);
export const IceCandidateJsonSchema = jsonSchema(IceCandidateSchema, 3000);
export const CallRecordSchema = v.object({
  id: IdSchema,
  caller: IdSchema,
  callee: IdSchema,
  media: CallMediaSchema,
  supportsScreenSharing: v.optional(v.boolean(invalid)),
  status: CallStatusSchema,
  offer: OfferTextSchema,
  answer: v.nullable(AnswerTextSchema),
  recovery: v.optional(v.nullable(CallRecoverySchema), null),
  created_at: v.number(),
  connected_at: v.nullable(v.number()),
  ended_at: v.nullable(v.number()),
  updated_at: v.number(),
});
export const CallHistorySchema = v.object({
  ...v.pick(CallRecordSchema, [
    "id",
    "caller",
    "callee",
    "media",
    "status",
    "created_at",
    "connected_at",
    "ended_at",
  ]).entries,
  person_id: IdSchema,
  ...v.pick(PersonSchema, ["name", "username", "color", "avatar", "guest"])
    .entries,
});
export const CallViewSchema = v.object({
  ...CallRecordSchema.entries,
  person: PersonSchema,
});
export const IceSignalSchema = v.object({
  id: v.pipe(v.number(), v.integer(), v.minValue(1)),
  sender: IdSchema,
  revision: v.optional(RevisionSchema, 0),
  payload: v.pipe(
    v.string(),
    v.check((text) => v.is(IceCandidateJsonSchema, text)),
  ),
});
export const RealtimeEventSchema = v.variant("type", [
  v.object({ type: v.literal("ready") }),
  v.object({ type: v.literal("sync") }),
  v.object({
    type: v.literal("presence"),
    personId: IdSchema,
    status: AvailabilitySchema,
    last_active_at: LastActiveSchema,
    call_notifications: v.boolean(),
  }),
  v.object({ type: v.literal("message"), message: ChatMessageSchema }),
  v.object({ type: v.literal("read"), personId: IdSchema }),
  v.object({
    type: v.literal("read-receipt"),
    personId: IdSchema,
    read_through: v.number(),
  }),
  v.object({ type: v.literal("call"), call: CallViewSchema }),
  v.object({
    type: v.literal("signal"),
    callId: IdSchema,
    signal: IceSignalSchema,
  }),
  v.object({ type: v.literal("error"), message: v.string() }),
  v.object({ type: v.literal("session-ended") }),
  // Another device got into this account.
  v.object({
    type: v.literal("new-sign-in"),
    device: v.string(),
    place: v.nullable(v.string()),
  }),
  // Another session added a way back into this account.
  v.object({
    type: v.literal("sign-in-method-changed"),
    method: v.picklist(["recovery", "passkey"]),
  }),
]);
export const ClientEventSchema = v.variant("type", [
  v.object({
    type: v.literal("signal"),
    callId: IdSchema,
    candidate: IceCandidateSchema,
    revision: v.optional(RevisionSchema, 0),
  }),
  v.object({ type: v.literal("call-alive"), callId: IdSchema }),
]);
export const ClientEventJsonSchema = jsonSchema(ClientEventSchema, 8192);
export const RealtimeEventJsonSchema = jsonSchema(RealtimeEventSchema, 64000);
export const MediaStateJsonSchema = jsonSchema(
  v.object({
    cameraOn: v.boolean(),
    sharingScreen: v.boolean(),
    muted: v.boolean(),
  }),
  100,
);
export const CallLinkSchema = v.object({
  id: CallLinkIdSchema,
  expires_at: v.number(),
});
export const GroupLinkSchema = v.object({
  id: GroupLinkIdSchema,
  expires_at: v.number(),
});
export const AuthenticatedSnapshotSchema = v.object({
  authenticated: v.literal(true),
  me: PersonSchema,
  passkeys: v.array(v.object({ id: v.string(), created_at: v.number() })),
  friends: v.array(PersonSchema),
  requests: v.array(PersonSchema),
  blocked: v.array(PersonSchema),
  history: v.array(CallHistorySchema),
  active: v.nullable(CallViewSchema),
  // The account's own unused call link.
  link: v.nullable(CallLinkSchema),
});
// A call-link visitor without an account, who may call only the link's host.
export const GuestSchema = v.object({
  id: IdSchema,
  name: v.string(),
  host: PersonSchema,
  expires_at: v.number(),
  // Set once the pass has ended: after a conversation, or a declined ring.
  outcome: v.nullable(v.picklist(["talked", "declined"])),
  active: v.nullable(CallViewSchema),
});
export const SnapshotSchema = v.variant("authenticated", [
  AuthenticatedSnapshotSchema,
  v.object({
    authenticated: v.literal(false),
    me: v.null(),
    guest: v.nullable(GuestSchema),
    suspended: v.optional(v.literal(true)),
  }),
]);
const IceUrlSchema = v.pipe(
  v.string(),
  v.regex(/^(?:stun|stuns|turn|turns):\S+$/),
);
const IceServerSchema = v.object({
  urls: v.union([IceUrlSchema, v.pipe(v.array(IceUrlSchema), v.minLength(1))]),
  username: v.optional(v.string()),
  credential: v.optional(v.string()),
});
export const TurnCredentialsSchema = v.object({
  iceServers: v.pipe(
    v.array(IceServerSchema),
    v.check((servers) =>
      servers.some((server) =>
        [server.urls].flat().some((url) => /^turns?:/.test(url)),
      ),
    ),
  ),
});
export const IceResponseSchema = v.object({
  ...TurnCredentialsSchema.entries,
  expiresAt: v.number(),
});
export const RegistrationProtectionSchema = v.object({
  required: v.boolean(),
  siteKey: v.nullable(v.string()),
});
// What the admin panel shows of an account. Credentials, link codes and
// conversations stay out of it, even for administrators.
export const AdminAccountSchema = v.object({
  id: IdSchema,
  name: v.string(),
  username: v.string(),
  bio: v.string(),
  created_at: v.number(),
  has_recovery: v.boolean(),
  passkeys: v.number(),
  sessions: v.number(),
  open_reports: v.number(),
  suspended_at: v.nullable(v.number()),
  administrator: v.boolean(),
});
export const AdminCallSchema = v.object({
  id: IdSchema,
  caller: IdSchema,
  callee: IdSchema,
  caller_name: v.string(),
  callee_name: v.string(),
  // The participant's id when they have an account, null for a guest.
  caller_account: v.nullable(IdSchema),
  callee_account: v.nullable(IdSchema),
  media: CallMediaSchema,
  status: CallStatusSchema,
  created_at: v.number(),
  connected_at: v.nullable(v.number()),
});
export const AdminGuestPassSchema = v.object({
  id: IdSchema,
  holder: IdSchema,
  name: v.string(),
  host: IdSchema,
  host_name: v.string(),
  outcome: v.nullable(v.picklist(["talked", "declined"])),
  created_at: v.number(),
  expires_at: v.number(),
});
export const AdminCallLinkSchema = v.object({
  id: CallLinkIdSchema,
  host: IdSchema,
  host_name: v.string(),
  created_at: v.number(),
  expires_at: v.number(),
});

export const ReportStatusSchema = v.picklist(["open", "resolved", "dismissed"]);
export const AdminReportSchema = v.object({
  id: IdSchema,
  reporter: IdSchema,
  target: IdSchema,
  reporter_name: v.string(),
  reporter_account: v.nullable(IdSchema),
  target_name: v.string(),
  // A guest keeps a trace while a pass or call names them. Nothing naming the
  // target means a deleted account or a guest whose pass was swept.
  target_kind: v.picklist(["account", "guest", "gone"]),
  target_suspended_at: v.nullable(v.number()),
  can_suspend: v.boolean(),
  reason: v.string(),
  status: ReportStatusSchema,
  created_at: v.number(),
  reviewed_at: v.nullable(v.number()),
});
export const moderationActions = [
  "suspend-account",
  "restore-account",
  "reset-profile",
  "end-sessions",
  "delete-account",
  "resolve-report",
  "dismiss-report",
  "end-call",
  "revoke-guest-pass",
  "invalidate-call-link",
] as const;
export const AdminAuditSchema = v.object({
  id: IdSchema,
  actor: IdSchema,
  actor_name: v.nullable(v.string()),
  target: v.string(),
  // Set while the target is an account; calls, passes and links have no page
  // once they end.
  target_name: v.nullable(v.string()),
  report_id: v.nullable(IdSchema),
  action: v.picklist(moderationActions),
  reason: v.string(),
  created_at: v.number(),
});
